// =============================================================================
// features/contracts/ContractSignModal.tsx — Confirmação de assinatura (F17-S05).
//
// Transição: draft → signed.
// Gate: caller verifica contracts:sign antes de abrir o modal.
//
// F46-S10: `blockedReason` é defesa em profundidade — o caller (ContractDetail)
// já desabilita o botão que abre este modal quando o gate de documentação
// (`documents.contract_signature_gate.enabled`, OFF por padrão) está ativo e
// a checklist está incompleta, mas se o modal abrir mesmo assim (ex.: dado
// já defasado em outra aba), o botão de confirmação some daqui também.
//
// DS:
//   - Overlay: bg-[var(--text)]/60 backdrop-blur-[4px] (padrão F5-S05).
//   - Diálogo: --elev-5, border --border, bg --bg-elev-1 (DS §9 modal).
//   - Bricolage Grotesque para título, Geist para corpo.
//   - Botão primário desabilitado durante isPending.
//
// LGPD: exibe apenas contract_reference — sem CPF, telefone ou email.
// =============================================================================

import * as React from 'react';

import { Button } from '../../components/ui/Button';

import type { Contract } from './schemas';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ContractSignModalProps {
  contract: Contract;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
  /** Não-nulo bloqueia a confirmação (gate de documentação, F46-S10). */
  blockedReason?: string | null;
  /**
   * F46-S32: tom do aviso — "pending" (falta um passo, cor --warning, nunca
   * --danger) ou "unavailable" (recurso indisponível — flag/permissão de
   * documentos, ou contrato sem `leadId` resolvido para o painel; tom
   * neutro, não é um aviso de pendência). Default 'pending' preserva o
   * comportamento anterior a este slot.
   */
  blockedReasonKind?: 'pending' | 'unavailable';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: string): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(parseFloat(value));
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function ContractSignModal({
  contract,
  onConfirm,
  onCancel,
  isPending,
  blockedReason = null,
  blockedReasonKind = 'pending',
}: ContractSignModalProps): React.JSX.Element {
  // O mesmo modal cobre as duas transições. Em `signed` a ação é a ATIVAÇÃO
  // (desembolso), que é quando as parcelas passam a existir — e isso precisa
  // estar dito na tela, senão o operador confirma sem saber o que vai acontecer.
  const isActivation = contract.status === 'signed';

  // Fechar com Escape
  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !isPending) onCancel();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPending, onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--text) 60%, transparent)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sign-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onCancel();
      }}
    >
      {/* Painel do diálogo — DS §9 modal: elev-5, border, bg-elev-1 */}
      <div
        className="w-full max-w-md rounded-md flex flex-col gap-6 p-6"
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--border)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
      >
        {/* Header */}
        <div className="flex flex-col gap-1.5">
          <h2
            id="sign-modal-title"
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
          >
            {isActivation ? 'Ativar contrato' : 'Marcar como assinado'}
          </h2>
          <p className="font-sans text-ink-2" style={{ fontSize: 'var(--text-sm)' }}>
            {isActivation ? (
              <>
                Esta ação confirma o desembolso, avança o status de <strong>Assinado</strong> para{' '}
                <strong>Ativo</strong> e <strong>gera as {contract.term_months} parcelas</strong> do
                contrato. Não pode ser desfeita diretamente.
              </>
            ) : (
              <>
                Esta ação registra a assinatura do contrato e avança o status de{' '}
                <strong>Rascunho</strong> para <strong>Assinado</strong>. Não pode ser desfeita
                diretamente.
              </>
            )}
          </p>
        </div>

        {/* Resumo do contrato */}
        <div
          className="rounded-sm px-4 py-3 flex flex-col gap-2"
          style={{ background: 'var(--bg-elev-2)', border: '1px solid var(--border)' }}
        >
          <div className="flex items-center justify-between">
            <span className="font-sans text-ink-3" style={{ fontSize: 'var(--text-xs)' }}>
              Referência
            </span>
            <span
              className="font-mono font-semibold text-azul"
              style={{ fontSize: 'var(--text-sm)' }}
            >
              {contract.contract_reference}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="font-sans text-ink-3" style={{ fontSize: 'var(--text-xs)' }}>
              Valor principal
            </span>
            <span className="font-mono font-bold text-ink" style={{ fontSize: 'var(--text-base)' }}>
              {formatCurrency(contract.principal_amount)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="font-sans text-ink-3" style={{ fontSize: 'var(--text-xs)' }}>
              Prazo
            </span>
            <span className="font-mono text-ink-2" style={{ fontSize: 'var(--text-sm)' }}>
              {contract.term_months} {contract.term_months === 1 ? 'mês' : 'meses'}
            </span>
          </div>

          {contract.first_due_date && (
            <div className="flex items-center justify-between">
              <span className="font-sans text-ink-3" style={{ fontSize: 'var(--text-xs)' }}>
                1ª parcela
              </span>
              <span className="font-mono text-ink-2" style={{ fontSize: 'var(--text-sm)' }}>
                {new Date(`${contract.first_due_date}T00:00:00`).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            </div>
          )}
        </div>

        {/* Aviso de gate de documentação (F46-S10) — só na transição draft → signed.
            F46-S32: "unavailable" (recurso indisponível — flag/permissão ou
            leadId não resolvido) usa tom neutro; "pending" (documentação
            reprovada/incompleta — falta um passo, nunca --danger) usa
            --warning. Contraste medido (não presumido) em ContractDetail.tsx. */}
        {!isActivation && blockedReason !== null && (
          <div
            className="rounded-sm px-3 py-2.5"
            style={
              blockedReasonKind === 'pending'
                ? { background: 'var(--warning-bg)', border: '1px solid var(--warning)' }
                : { background: 'var(--bg-elev-2)', border: '1px solid var(--border)' }
            }
            role="status"
          >
            <p
              className="font-sans"
              style={{
                fontSize: 'var(--text-xs)',
                color: blockedReasonKind === 'pending' ? 'var(--warning)' : 'var(--text-2)',
              }}
            >
              {blockedReason}
            </p>
          </div>
        )}

        {/* Ações */}
        <div className="flex flex-col gap-2.5">
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={isPending || (!isActivation && blockedReason !== null)}
            className="w-full justify-center"
          >
            {isPending
              ? isActivation
                ? 'Ativando...'
                : 'Assinando...'
              : isActivation
                ? 'Confirmar ativação'
                : 'Confirmar assinatura'}
          </Button>
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={isPending}
            className="w-full justify-center"
          >
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
}
